import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import GlobeCanvas from "../components/GlobeCanvas";
import { SiteHeader, SiteFooter, Icon } from "../components/SiteChrome";
import LocationSearch from "../components/LocationSearch";
import SavedPlaces from "../components/SavedPlaces";
import useFavorites from "../hooks/useFavorites";
import { EXAMPLE_LOCATIONS, monitorPath } from "../lib/locations";

const modules = [
  {
    id: "air",
    title: "Air quality",
    source: "OPENWEATHER · CAMS",
    desc: "Put the air around you in perspective. Explore pollutants, air-quality indices and forecasts.",
    color: "teal",
  },
  {
    id: "climate",
    title: "Weather",
    source: "OPEN-METEO",
    desc: "From current model estimates to the week ahead: temperature, rain, humidity and wind.",
    color: "amber",
  },
  {
    id: "flood",
    title: "River flow",
    source: "GLOFAS",
    desc: "Follow river discharge with recent history, a 14-day outlook and forecast uncertainty.",
    color: "blue",
  },
  {
    id: "veg",
    title: "Soil & water",
    source: "OPEN-METEO",
    desc: "Look beneath the surface with shallow soil moisture, rainfall and water-demand indicators.",
    color: "green",
  },
  {
    id: "drought",
    title: "Drought context",
    source: "HISTORICAL WEATHER",
    desc: "Explore recent rainfall and climatic water balance over the past 30 and 90 days.",
    color: "violet",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [globePaused, setGlobePaused] = useState(false);
  const { favorites, toggle, message } = useFavorites();
  const explore = (place) => navigate(monitorPath(place));
  return (
    <div className="site-page">
      <SiteHeader />
      <main id="main-content">
        <section className="hero-section">
          <div className="landing-hero site-container">
            <div className="hero-copy">
              <div className="hero-label">
                <span className="tiny-dot" /> A LITTLE CONTEXT. A BIGGER
                PICTURE.
              </div>
              <h1>
                Your world.
                <br />A <span>clearer view.</span>
              </h1>
              <p>
                Explore air quality, weather, rivers and soil conditions
                anywhere. Start with a place that matters to you.
              </p>
              <div className="hero-search">
                <LocationSearch onSelect={explore} />
                <div className="example-places">
                  <span>Try a place</span>
                  {[
                    EXAMPLE_LOCATIONS[0],
                    EXAMPLE_LOCATIONS[3],
                    EXAMPLE_LOCATIONS[7],
                  ].map((place) => (
                    <Link to={monitorPath(place)} key={place.name}>
                      {place.name}
                      <Icon name="arrow" size={14} />
                    </Link>
                  ))}
                </div>
                <SavedPlaces
                  places={favorites}
                  onSelect={explore}
                  onRemove={toggle}
                />
                {message && (
                  <p className="saved-status" role="status">
                    {message}
                  </p>
                )}
              </div>
              <div className="hero-caption">
                <span>
                  <Icon name="globe" size={16} /> Global models
                </span>
                <span>
                  <Icon name="layers" size={16} /> Five perspectives
                </span>
                <span>
                  <Icon name="check" size={16} /> Sources included
                </span>
              </div>
            </div>
            <div className="globe-panel">
              <div className="globe-kicker">
                <span>THE BIG PICTURE</span>
                <span>01 — 05</span>
              </div>
              <GlobeCanvas paused={globePaused} />
              <div className="globe-badge globe-badge-air">
                <span className="badge-icon teal">
                  <Icon name="air" />
                </span>
                <span>
                  <b>Atmosphere</b>
                  <small>Air & weather</small>
                </span>
              </div>
              <div className="globe-badge globe-badge-land">
                <span className="badge-icon violet">
                  <Icon name="veg" />
                </span>
                <span>
                  <b>Land & water</b>
                  <small>Connected perspectives</small>
                </span>
              </div>
              <div className="globe-caption">
                <span className="tiny-dot" /> One planet. Many signals.
                <button
                  className="globe-motion"
                  type="button"
                  aria-pressed={globePaused}
                  onClick={() => setGlobePaused((value) => !value)}
                >
                  {globePaused ? "Resume globe" : "Pause globe"}
                </button>
              </div>
            </div>
          </div>
        </section>
        <section
          className="provider-strip"
          aria-label="Environmental data providers"
        >
          <div className="site-container provider-inner">
            <span>
              BUILT ON ESTABLISHED
              <br />
              ENVIRONMENTAL DATA
            </span>
            <b>OpenWeather</b>
            <b>Open-Meteo</b>
            <b>
              Copernicus <small>CAMS</small>
            </b>
            <b>GloFAS</b>
          </div>
        </section>
        <section className="modules-section site-container" id="modules">
          <div className="section-intro">
            <div>
              <div className="eyebrow">FIVE WAYS TO EXPLORE</div>
              <h2>Five ways to understand your environment.</h2>
            </div>
            <p>
              From the air above you to the soil beneath you. Every result comes
              with its source, timestamp and context.
            </p>
          </div>
          <div className="module-card-grid">
            {modules.map((mod, i) => (
              <Link
                className={`module-card ${mod.color}`}
                to={`/monitor?module=${mod.id}`}
                key={mod.id}
              >
                <div className="module-card-top">
                  <span className="module-icon">
                    <Icon name={mod.id} size={25} />
                  </span>
                  <span>0{i + 1}</span>
                </div>
                <h3>{mod.title}</h3>
                <div className="module-source">{mod.source}</div>
                <p>{mod.desc}</p>
                <span className="module-open">
                  Explore <Icon name="arrow" size={18} />
                </span>
              </Link>
            ))}
          </div>
        </section>
        <section className="trust-section">
          <div className="site-container trust-inner">
            <div>
              <div className="eyebrow">CLARITY, BY DESIGN</div>
              <h2>
                Good decisions start
                <br />
                with understanding
                <br />
                <span>the data.</span>
              </h2>
              <Link className="light-button" to="/modules#data">
                Meet the sources <Icon name="arrow" size={18} />
              </Link>
            </div>
            <div className="trust-details">
              <article>
                <span className="trust-icon">
                  <Icon name="clock" />
                </span>
                <div>
                  <h3>Freshness you can see</h3>
                  <p>
                    Data time and fetch time are shown separately. The monitor
                    checks for updates every five minutes while visible; each
                    provider has its own update schedule.
                  </p>
                </div>
              </article>
              <article>
                <span className="trust-icon">
                  <Icon name="layers" />
                </span>
                <div>
                  <h3>Models with their context</h3>
                  <p>
                    Current estimates, forecasts and historical records are
                    labelled. A model grid represents an area, rather than a
                    sensor at every point.
                  </p>
                </div>
              </article>
              <article>
                <span className="trust-icon">
                  <Icon name="check" />
                </span>
                <div>
                  <h3>A clear view of what is available</h3>
                  <p>
                    Missing, older or unavailable data is flagged. A refresh
                    checks the source; it does not create a new measurement.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
        <section className="faq-section site-container">
          <div>
            <div className="eyebrow">A LITTLE MORE CONTEXT</div>
            <h2>Before you explore.</h2>
            <p>Useful answers about the view ahead.</p>
          </div>
          <div className="faq-list">
            <details>
              <summary>Is AirVista showing real-time data?</summary>
              <p>
                AirVista requests real external APIs. Most results are model
                estimates, forecasts or historical data, rather than a live
                sensor stream. Source timestamps and update schedules help you
                judge how current each result is.
              </p>
            </details>
            <details>
              <summary>Can I explore any location?</summary>
              <p>
                You can choose global coordinates. Availability and detail vary
                by provider and model. River data may represent a nearby river
                grid, while some locations may have missing results.
              </p>
            </details>
            <details>
              <summary>How should I use the information?</summary>
              <p>
                Use AirVista to explore and compare environmental context.
                Models can differ from local observations. For safety-critical
                decisions, consult local authorities and official alerts.
              </p>
            </details>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
